import type { RenderContext, SectionProps } from "../context";
import { field, titleId } from "../context";
import { Balloons, Confetti, Petals, Sparkles, Squiggle } from "../decor";
import { socialLinks } from "../helpers";
import { Icon } from "../icons";
import {
  Container,
  CtaButton,
  Heading,
  EditorImagePlaceholder,
  Eyebrow,
  Img,
  MultiLine,
  Ornament,
  SectionShell,
  ctaVisible,
  SiteLink,
  cx,
  enterDelay,
  headingTag,
  isPriority,
  Marquee as UiMarquee,
  type ButtonStyle,
} from "../ui";

type HeroNode = SectionProps<"hero">["node"];

function HeroTitle({ ctx, node, className }: { ctx: RenderContext; node: HeroNode; className: string }) {
  return (
    <Heading level={headingTag(ctx, node)} id={titleId(ctx, node)} className={cx("baze-heading baze-enter text-balance", className)} style={enterDelay(1)} {...field(ctx, "content.title")}>
      {node.content.title}
    </Heading>
  );
}

function HeroEyebrow({ ctx, node, className }: { ctx: RenderContext; node: HeroNode; className?: string }) {
  if (!node.content.eyebrow) return null;
  return (
    <div className="baze-enter" style={enterDelay(0)}>
      <Eyebrow ctx={ctx} text={node.content.eyebrow} path="content.eyebrow" className={className} />
    </div>
  );
}

function HeroDescription({ ctx, node, className }: { ctx: RenderContext; node: HeroNode; className?: string }) {
  if (!node.content.description) return null;
  return (
    <div className="baze-enter" style={enterDelay(2)}>
      <MultiLine ctx={ctx} text={node.content.description} path="content.description" className={className} />
    </div>
  );
}

function HeroActions({ ctx, node, center, secondary = "secondary" }: { ctx: RenderContext; node: HeroNode; center?: boolean; secondary?: ButtonStyle }) {
  const c = node.content;
  // Live sites drop buttons without a working link; drop the spacing wrapper with them.
  if (!ctaVisible(ctx, c.primaryCta) && !ctaVisible(ctx, c.secondaryCta)) return null;
  return (
    <div className={cx("baze-enter mt-8 flex flex-col gap-3 @sm:flex-row @sm:flex-wrap", center && "@sm:justify-center")} style={enterDelay(3)}>
      <CtaButton ctx={ctx} cta={c.primaryCta} path="content.primaryCta" style="primary" />
      <CtaButton ctx={ctx} cta={c.secondaryCta} path="content.secondaryCta" style={secondary} withArrow />
    </div>
  );
}

function Locale({ ctx, light, center }: { ctx: RenderContext; light?: boolean; center?: boolean }) {
  const city = ctx.site.contact.city;
  if (!city) return null;
  return (
    <p className={cx("baze-enter mt-6 flex items-center gap-1.5 text-sm", light ? "text-white/80" : "text-site-muted", center && "justify-center")} style={enterDelay(4)}>
      <Icon name="map-pin" className="size-4" />
      <span>{city}</span>
    </p>
  );
}

/**
 * Soft blurred colour fields behind light heroes. Decorative; stays inside the section, so its clipped edge
 * reads as a band: heroes that use it (or the grid) are `baze-band` and keep their own padding.
 */
function Glow({ className }: { className?: string }) {
  return (
    <div aria-hidden="true" className={cx("pointer-events-none absolute inset-0 -z-10 overflow-hidden", className)}>
      <div className="absolute -top-32 -right-24 size-[28rem] rounded-full bg-site-secondary/25 blur-3xl" />
      <div className="absolute -bottom-40 -left-32 size-[26rem] rounded-full bg-site-primary/10 blur-3xl" />
    </div>
  );
}

function Centered({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <Glow />
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
          <HeroTitle ctx={ctx} node={node} className="text-[2.5rem] leading-[1.05] @md:text-5xl @3xl:text-6xl @5xl:text-[4.25rem]" />
          <HeroDescription ctx={ctx} node={node} className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-site-muted @3xl:text-xl" />
          <HeroActions ctx={ctx} node={node} center />
          <Locale ctx={ctx} center />
        </div>
        {c.image ? (
          <div className="baze-enter-pop mt-12 aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg @3xl:mt-16 @3xl:aspect-[21/9]" style={enterDelay(3)}>
            <Img ctx={ctx} image={c.image} sizes="(min-width: 1200px) 1120px, 100vw" priority={isPriority(ctx, node)} path="content.image" target={1600} />
          </div>
        ) : null}
      </Container>
    </SectionShell>
  );
}

function Split({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <Glow />
      <Container>
        <div className={cx("grid items-center gap-12 @4xl:gap-14 @5xl:gap-20", hasImage && "@4xl:grid-cols-[1.05fr_1fr]")}>
          <div className={cx(!hasImage && "max-w-3xl")}>
            <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
            <HeroTitle ctx={ctx} node={node} className="text-[2.5rem] leading-[1.05] @md:text-5xl @5xl:text-[3.75rem]" />
            <HeroDescription ctx={ctx} node={node} className="mt-6 max-w-xl text-lg leading-relaxed text-site-muted" />
            <HeroActions ctx={ctx} node={node} />
            <Locale ctx={ctx} />
          </div>
          {hasImage ? (
            <div className="baze-enter-pop relative" style={enterDelay(2)}>
              <div aria-hidden="true" className="absolute -inset-3 -z-0 rotate-2 rounded-site-lg bg-site-secondary/25 @3xl:-inset-4" />
              <div aria-hidden="true" className="baze-float absolute -top-6 -left-6 hidden size-24 rounded-full border border-site-primary/25 @4xl:block" />
              <div className="relative aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg @4xl:aspect-[4/5]">
                {c.image ? (
                  <Img ctx={ctx} image={c.image} sizes="(min-width: 1024px) 560px, 100vw" priority={isPriority(ctx, node)} path="content.image" target={1600} />
                ) : (
                  <EditorImagePlaceholder ctx={ctx} path="content.image" />
                )}
              </div>
            </div>
          ) : null}
        </div>
      </Container>
    </SectionShell>
  );
}

function ImageBackground(props: SectionProps<"hero">) {
  const { node, ctx } = props;
  const c = node.content;
  if (!c.image) return <Centered {...props} />;
  return (
    <SectionShell ctx={ctx} node={node} padded={false} className="baze-hero relative isolate overflow-hidden bg-site-inverted!">
      <div className="absolute inset-0 -z-10 overflow-hidden">
        <div className="baze-kenburns h-full w-full">
          <Img ctx={ctx} image={c.image} sizes="100vw" priority={isPriority(ctx, node)} path="content.image" target={1600} />
        </div>
      </div>
      <div aria-hidden="true" className="absolute inset-0 -z-10 bg-linear-to-t from-black/85 via-black/50 to-black/20 @3xl:bg-linear-to-r @3xl:from-black/80 @3xl:via-black/45 @3xl:to-black/10" />
      <Container className="flex min-h-[34rem] items-end py-(--site-section-y) @3xl:min-h-[40rem] @3xl:items-center @3xl:py-(--site-section-y-lg) @5xl:min-h-[44rem]">
        <div className="max-w-2xl text-white">
          <HeroEyebrow ctx={ctx} node={node} className="mb-5 text-white/85!" />
          <HeroTitle ctx={ctx} node={node} className="text-[2.5rem] leading-[1.05] text-white @md:text-5xl @3xl:text-6xl @5xl:text-[4.25rem]" />
          <HeroDescription ctx={ctx} node={node} className="mt-6 max-w-xl text-lg leading-relaxed text-white/85 @3xl:text-xl" />
          <HeroActions ctx={ctx} node={node} secondary="glass" />
          <Locale ctx={ctx} light />
        </div>
      </Container>
      <div aria-hidden="true" className="baze-enter absolute bottom-6 left-1/2 hidden -translate-x-1/2 flex-col items-center gap-2 text-white/70 @3xl:flex" style={enterDelay(6)}>
        <span className="text-[0.6875rem] font-semibold tracking-[0.3em] uppercase">Gulir</span>
        <span className="baze-float block h-10 w-px bg-white/50" />
      </div>
    </SectionShell>
  );
}

function Minimal({ node, ctx }: SectionProps<"hero">) {
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <div aria-hidden="true" className="baze-grid-lines pointer-events-none absolute inset-0 -z-10 text-site-text [mask-image:radial-gradient(ellipse_at_top_right,black,transparent_70%)]" />
      <Container>
        <HeroEyebrow ctx={ctx} node={node} className="mb-6" />
        <HeroTitle ctx={ctx} node={node} className="max-w-5xl text-[2.75rem] leading-[1.02] @md:text-6xl @3xl:text-7xl @5xl:text-[5.5rem]" />
        <div className="mt-10 grid gap-8 border-t border-site-border pt-8 @4xl:grid-cols-[1.2fr_1fr] @4xl:items-end">
          <HeroDescription ctx={ctx} node={node} className="max-w-xl text-lg leading-relaxed text-site-muted @3xl:text-xl" />
          <div className="@4xl:justify-self-end">
            <HeroActions ctx={ctx} node={node} />
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

/** Arched portrait photo with a hairline frame: weddings, salons, villas. */
function Arch({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <Glow />
      <Container>
        <div className={cx("grid items-center gap-12 @4xl:gap-20", hasImage && "@4xl:grid-cols-[1fr_1.1fr]")}>
          {hasImage ? (
            <div className="baze-enter-pop relative mx-auto w-full max-w-[16.5rem] @md:max-w-[20rem] @4xl:order-2 @4xl:max-w-[28rem]" style={enterDelay(1)}>
              <div aria-hidden="true" className="absolute -inset-3 rounded-t-full rounded-b-site-lg border border-site-primary/35 @3xl:-inset-4" />
              <div aria-hidden="true" className="baze-spin-slow absolute -top-8 -right-8 hidden size-28 rounded-full border border-dashed border-site-primary/30 @3xl:block" />
              <div className="relative aspect-[3/4] overflow-hidden rounded-t-full rounded-b-site-lg bg-site-surface shadow-site-lg">
                {c.image ? (
                  <Img ctx={ctx} image={c.image} sizes="(min-width: 1024px) 448px, 352px" priority={isPriority(ctx, node)} path="content.image" target={960} />
                ) : (
                  <EditorImagePlaceholder ctx={ctx} path="content.image" />
                )}
              </div>
              <span aria-hidden="true" className="absolute -bottom-5 left-1/2 grid size-11 -translate-x-1/2 place-items-center rounded-full bg-site-primary text-site-on-primary shadow-site-md">
                <Icon name="sparkle" className="size-5" />
              </span>
            </div>
          ) : null}
          <div className={cx("text-center @4xl:text-left", !hasImage && "mx-auto max-w-3xl @4xl:text-center")}>
            <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
            <HeroTitle ctx={ctx} node={node} className="text-[2.5rem] leading-[1.08] @md:text-5xl @5xl:text-[4rem]" />
            <div className={cx("baze-enter mt-6 flex justify-center", hasImage && "@4xl:justify-start")} style={enterDelay(2)}>
              <Ornament className="w-48" />
            </div>
            <HeroDescription ctx={ctx} node={node} className={cx("mx-auto mt-6 max-w-xl text-lg leading-relaxed text-site-muted", hasImage && "@4xl:mx-0")} />
            <HeroActions ctx={ctx} node={node} center={!hasImage} />
            <Locale ctx={ctx} center={!hasImage} />
          </div>
        </div>
      </Container>
    </SectionShell>
  );
}

/** Magazine layout: oversized headline, copy column, then a wide photo with a caption chip. */
function Editorial({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const city = ctx.site.contact.city;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero relative isolate overflow-hidden">
      <Container size="wide">
        <div className="grid gap-8 @4xl:grid-cols-[1.6fr_1fr] @4xl:items-end @4xl:gap-14">
          <div>
            <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
            <HeroTitle ctx={ctx} node={node} className="text-[2.75rem] leading-[1] @md:text-6xl @3xl:text-7xl @6xl:text-[5.75rem]" />
          </div>
          <div className="@4xl:pb-2">
            <HeroDescription ctx={ctx} node={node} className="text-lg leading-relaxed text-site-muted" />
            <HeroActions ctx={ctx} node={node} />
          </div>
        </div>
        {c.image || ctx.editor ? (
          <div className="baze-enter-pop relative mt-12 @3xl:mt-16" style={enterDelay(3)}>
            <div className="aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg @3xl:aspect-[21/9]">
              {c.image ? (
                <div className="baze-kenburns h-full w-full">
                  <Img ctx={ctx} image={c.image} sizes="(min-width: 1280px) 1216px, 100vw" priority={isPriority(ctx, node)} path="content.image" target={1600} />
                </div>
              ) : (
                <EditorImagePlaceholder ctx={ctx} path="content.image" />
              )}
            </div>
            {city ? (
              <p className="absolute bottom-4 left-4 inline-flex items-center gap-2 rounded-full bg-site-bg/90 px-4 py-2 text-sm font-semibold text-site-text shadow-site-md backdrop-blur @3xl:bottom-6 @3xl:left-6">
                <Icon name="map-pin" className="size-4 text-site-primary" />
                {city}
              </p>
            ) : null}
          </div>
        ) : null}
      </Container>
    </SectionShell>
  );
}

/** Link-in-bio header: round avatar, name, short bio, social icons. Compact so links sit above the fold. */
function Profile({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const socials = socialLinks(ctx.site);
  return (
    <SectionShell ctx={ctx} node={node} padded={false} className="baze-hero relative isolate overflow-hidden pt-12 pb-6 @3xl:pt-16 @3xl:pb-8">
      <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 -z-10 h-56 bg-linear-to-b from-site-secondary/35 to-transparent" />
      <Container size="narrow" className="text-center">
        {c.image || ctx.editor ? (
          <div className="baze-enter-pop relative mx-auto size-28 @3xl:size-32" style={enterDelay(0)}>
            <div aria-hidden="true" className="baze-spin-slow absolute -inset-1.5 rounded-full border-2 border-dashed border-site-primary/40" />
            <div className="relative size-full overflow-hidden rounded-full bg-site-surface shadow-site-md ring-4 ring-site-bg">
              {c.image ? (
                <Img ctx={ctx} image={c.image} sizes="128px" target={480} priority={isPriority(ctx, node)} path="content.image" />
              ) : (
                <EditorImagePlaceholder ctx={ctx} path="content.image" />
              )}
            </div>
          </div>
        ) : null}
        <HeroEyebrow ctx={ctx} node={node} className="mt-6" />
        <HeroTitle ctx={ctx} node={node} className="mt-3 text-[2rem] leading-tight @3xl:text-[2.5rem]" />
        <HeroDescription ctx={ctx} node={node} className="mx-auto mt-3 max-w-md text-base leading-relaxed text-site-muted" />
        {socials.length > 0 ? (
          <ul className="baze-enter mt-5 flex justify-center gap-2" style={enterDelay(3)}>
            {socials.map((s) => (
              <li key={s.kind}>
                <SiteLink
                  ctx={ctx}
                  href={s.href}
                  trackLink
                  ariaLabel={`${s.label} ${ctx.site.name}`}
                  className="grid size-11 place-items-center rounded-full border border-site-border bg-site-surface text-site-text transition hover:-translate-y-0.5 hover:border-site-primary hover:text-site-primary motion-reduce:transition-none"
                >
                  <Icon name={s.kind} className="size-[1.125rem]" />
                </SiteLink>
              </li>
            ))}
          </ul>
        ) : null}
        <HeroActions ctx={ctx} node={node} center />
      </Container>
    </SectionShell>
  );
}

/**
 * Party hero for birthdays, launches and festive promos: pastel glow, falling confetti, balloons and
 * sparkles (CSS only), an optional round photo with a waving party badge, and a hand-drawn squiggle.
 */
function Celebration({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-0 -z-10"
        style={{
          background:
            "radial-gradient(55% 45% at 15% 10%, color-mix(in srgb, var(--site-secondary) 55%, transparent), transparent 70%), radial-gradient(50% 45% at 90% 15%, color-mix(in srgb, var(--site-accent) 35%, transparent), transparent 70%), radial-gradient(60% 50% at 50% 100%, color-mix(in srgb, var(--site-primary) 14%, transparent), transparent 70%)",
        }}
      />
      <Confetti className="-z-10" />
      <Balloons className="-z-10" />
      <Container size="narrow" className="relative text-center">
        {c.image || ctx.editor ? (
          <div className="baze-enter-pop relative mx-auto mb-8 size-36 @3xl:size-44" style={enterDelay(0)}>
            <div aria-hidden="true" className="baze-spin-slow absolute -inset-2.5 rounded-full border-[3px] border-dashed border-site-accent/70" />
            <div className="relative size-full overflow-hidden rounded-full bg-site-surface shadow-site-lg ring-[6px] ring-site-bg">
              {c.image ? (
                <Img ctx={ctx} image={c.image} sizes="176px" target={480} priority={isPriority(ctx, node)} path="content.image" />
              ) : (
                <EditorImagePlaceholder ctx={ctx} path="content.image" />
              )}
            </div>
            <span aria-hidden="true" className="baze-wiggle absolute -top-1 -right-2 grid size-12 place-items-center rounded-full bg-site-primary text-site-on-primary shadow-site-md @3xl:size-14">
              <Icon name="party" className="size-6 @3xl:size-7" />
            </span>
          </div>
        ) : null}
        <div className="relative">
          <Sparkles className="-inset-x-6 -inset-y-4" />
          <div className="baze-enter" style={enterDelay(1)}>
            <Eyebrow ctx={ctx} text={c.eyebrow} path="content.eyebrow" className="mb-5 inline-flex rounded-full bg-site-surface/85 px-4 py-1.5 tracking-[0.14em] shadow-site-sm ring-1 ring-site-border" />
          </div>
          <HeroTitle ctx={ctx} node={node} className="text-[2.75rem] leading-[1.02] @md:text-6xl @3xl:text-7xl" />
          <div className="baze-enter mt-4" style={enterDelay(2)}>
            <Squiggle />
          </div>
        </div>
        <HeroDescription ctx={ctx} node={node} className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-site-muted @3xl:text-xl" />
        <HeroActions ctx={ctx} node={node} center />
        <Locale ctx={ctx} center />
      </Container>
    </SectionShell>
  );
}

/**
 * Invitation cover: a full-screen opening with the couple's names, "Kepada Yth." and the guest's name.
 * The guest name is a placeholder ("Tamu Undangan"); on live sites the runtime fills it from the link's
 * `?to=` parameter (personal links from the dashboard). The primary button opens the invitation (scrolls
 * to the content). Photo covers get a dark overlay; without a photo, a soft glow. Petals drift down.
 */
function Cover({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const photo = !!c.image;
  const en = ctx.site.locale === "en-US";
  return (
    <SectionShell
      ctx={ctx}
      node={node}
      padded={false}
      className={cx("baze-hero baze-band relative isolate flex items-center overflow-hidden py-20 text-center", ctx.editor || ctx.thumbnail ? "min-h-[40rem]" : "min-h-[100svh]", photo && "bg-site-inverted!")}
    >
      {photo ? (
        <>
          <div className="absolute inset-0 -z-10 overflow-hidden">
            <div className="baze-kenburns h-full w-full">
              <Img ctx={ctx} image={c.image!} sizes="100vw" priority={isPriority(ctx, node)} path="content.image" target={1600} />
            </div>
          </div>
          <div aria-hidden="true" className="absolute inset-0 -z-10 bg-linear-to-b from-black/55 via-black/45 to-black/75" />
        </>
      ) : (
        <Glow />
      )}
      <Petals className="-z-10" />
      <Container size="narrow" className={cx("relative", photo ? "text-white" : "text-site-text")}>
        <HeroEyebrow ctx={ctx} node={node} className={cx("mb-6 tracking-[0.3em]", photo && "text-white/85!")} />
        <HeroTitle ctx={ctx} node={node} className={cx("text-[2.75rem] leading-[1.05] @md:text-6xl @3xl:text-7xl", photo && "text-white")} />
        <div className="baze-enter mt-6" style={enterDelay(2)}>
          <Ornament className={cx("mx-auto", photo && "text-white/70!")} />
        </div>
        <HeroDescription ctx={ctx} node={node} className={cx("mx-auto mt-6 max-w-lg text-lg leading-relaxed", photo ? "text-white/85" : "text-site-muted")} />
        <div
          className={cx("baze-enter mx-auto mt-10 inline-flex min-w-64 flex-col items-center rounded-site-lg px-8 py-5 ring-1 backdrop-blur-sm", photo ? "bg-white/10 ring-white/25" : "bg-site-surface/80 ring-site-border shadow-site-sm")}
          style={enterDelay(3)}
        >
          <span className={cx("text-[0.6875rem] font-semibold tracking-[0.22em] uppercase", photo ? "text-white/75" : "text-site-muted")}>{en ? "Dear" : "Kepada Yth."}</span>
          <span className={cx("mt-1 text-sm", photo ? "text-white/80" : "text-site-muted")}>{en ? "our honoured guest" : "Bapak/Ibu/Saudara/i"}</span>
          <span data-baze-guest="" className={cx("baze-heading mt-2 max-w-full text-2xl font-semibold break-words", photo ? "text-white" : "text-site-text")}>
            {en ? "Guest" : "Tamu Undangan"}
          </span>
        </div>
        <HeroActions ctx={ctx} node={node} center secondary={photo ? "glass" : "secondary"} />
      </Container>
      <div aria-hidden="true" className={cx("baze-enter absolute bottom-6 left-1/2 flex -translate-x-1/2 flex-col items-center gap-2", photo ? "text-white/70" : "text-site-muted")} style={enterDelay(6)}>
        <span className="text-[0.6875rem] font-semibold tracking-[0.3em] uppercase">{en ? "Scroll" : "Gulir"}</span>
        <span className={cx("baze-float block h-10 w-px", photo ? "bg-white/50" : "bg-site-border")} />
      </div>
    </SectionShell>
  );
}

/**
 * Kinetic poster: an edge-to-edge headline, a primary-colour ticker repeating the eyebrow (or the site
 * name), then copy and buttons in two columns above a wide photo. The ticker is decorative.
 */
function Marquee({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const word = c.eyebrow || ctx.site.name;
  const run = (
    <span className="flex shrink-0 items-center gap-6 pr-6 whitespace-nowrap @3xl:gap-10 @3xl:pr-10">
      {[0, 1, 2, 3].map((i) => (
        <span key={i} className="flex items-center gap-6 @3xl:gap-10">
          <span>{word}</span>
          <Icon name="sparkle" className="size-5 shrink-0 @3xl:size-7" />
        </span>
      ))}
    </span>
  );
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <Container size="wide">
        <HeroEyebrow ctx={ctx} node={node} className="mb-6" />
        <HeroTitle ctx={ctx} node={node} className="max-w-[18ch] text-[2.5rem] leading-[0.98] @md:text-6xl @3xl:text-7xl @5xl:text-[6.5rem]" />
      </Container>
      <div aria-hidden="true" className="baze-enter -mx-4 mt-10 -rotate-1 @3xl:mt-14" style={enterDelay(2)}>
        <UiMarquee
          seconds={Math.min(60, Math.max(20, word.length * 1.2))}
          repeat={run}
          className="baze-heading border-y-2 border-site-text bg-site-primary py-3 text-2xl leading-none text-site-on-primary @3xl:py-4 @3xl:text-4xl"
        >
          {run}
        </UiMarquee>
      </div>
      <Container size="wide" className="mt-10 @3xl:mt-14">
        <div className="grid gap-6 @4xl:grid-cols-[1.2fr_1fr] @4xl:items-end @4xl:gap-14">
          <HeroDescription ctx={ctx} node={node} className="max-w-xl text-lg leading-relaxed text-site-muted @3xl:text-xl" />
          <div className="@4xl:justify-self-end">
            <HeroActions ctx={ctx} node={node} />
            <Locale ctx={ctx} />
          </div>
        </div>
        {c.image || ctx.editor ? (
          <div className="baze-enter-pop mt-12 aspect-[4/3] overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg @3xl:mt-16 @3xl:aspect-[21/9]" style={enterDelay(4)}>
            {c.image ? (
              <div className="baze-kenburns h-full w-full">
                <Img ctx={ctx} image={c.image} sizes="(min-width: 1280px) 1216px, 100vw" priority={isPriority(ctx, node)} path="content.image" target={1600} />
              </div>
            ) : (
              <EditorImagePlaceholder ctx={ctx} path="content.image" />
            )}
          </div>
        ) : null}
      </Container>
    </SectionShell>
  );
}

/**
 * Scrapbook / Bauhaus collage: the photo as a tilted print with tape corners, geometric shapes in the
 * secondary and accent colours, and a spinning sticker. Copy sits beside it like Split.
 */
function Collage({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <div aria-hidden="true" className="baze-dots pointer-events-none absolute inset-0 -z-10 text-site-text opacity-60 [mask-image:radial-gradient(ellipse_at_70%_40%,black,transparent_70%)]" />
      <Container>
        <div className={cx("grid items-center gap-14 @4xl:gap-16", hasImage && "@4xl:grid-cols-[1fr_1fr]")}>
          <div className={cx(!hasImage && "max-w-3xl")}>
            <HeroEyebrow ctx={ctx} node={node} className="mb-5 inline-flex -rotate-2 rounded-site-sm bg-site-secondary/30 px-3 py-1.5" />
            <HeroTitle ctx={ctx} node={node} className="text-[2.5rem] leading-[1.04] @md:text-5xl @5xl:text-[4rem]" />
            <HeroDescription ctx={ctx} node={node} className="mt-6 max-w-xl text-lg leading-relaxed text-site-muted" />
            <HeroActions ctx={ctx} node={node} />
            <Locale ctx={ctx} />
          </div>
          {hasImage ? (
            <div className="baze-enter-pop relative mx-auto w-full max-w-[18rem] @md:max-w-sm @4xl:max-w-none" style={enterDelay(2)}>
              <div aria-hidden="true" className="absolute -top-6 -left-4 size-28 rounded-full bg-site-secondary @3xl:-top-10 @3xl:-left-10 @3xl:size-40" />
              <div aria-hidden="true" className="absolute -right-3 -bottom-6 size-24 rotate-12 bg-site-accent @3xl:-right-8 @3xl:-bottom-8 @3xl:size-32" />
              <div aria-hidden="true" className="absolute top-1/2 -right-6 hidden h-0 w-0 border-y-[2.25rem] border-l-[3.5rem] border-y-transparent border-l-site-primary/80 @4xl:block" />
              <div className="relative -rotate-3 bg-white p-3 pb-14 shadow-site-lg transition-transform duration-500 hover:rotate-0 motion-reduce:transition-none @3xl:p-4 @3xl:pb-16">
                <span aria-hidden="true" className="absolute -top-3 left-6 h-7 w-24 -rotate-6 bg-site-secondary/70 mix-blend-multiply" />
                <span aria-hidden="true" className="absolute -top-3 right-6 h-7 w-20 rotate-6 bg-site-accent/60 mix-blend-multiply" />
                <div className="aspect-square overflow-hidden bg-site-surface @4xl:aspect-[4/5]">
                  {c.image ? (
                    <Img ctx={ctx} image={c.image} sizes="(min-width: 1024px) 520px, 90vw" priority={isPriority(ctx, node)} path="content.image" target={1200} />
                  ) : (
                    <EditorImagePlaceholder ctx={ctx} path="content.image" />
                  )}
                </div>
              </div>
              <span aria-hidden="true" className="baze-wiggle absolute -bottom-6 -left-4 grid size-20 place-items-center rounded-full bg-site-primary text-site-on-primary shadow-site-md ring-4 ring-site-bg @3xl:size-24">
                <Icon name="sparkle" className="size-8 @3xl:size-10" />
              </span>
            </div>
          ) : null}
        </div>
      </Container>
    </SectionShell>
  );
}

/**
 * Product shot on a lit stage: headline, then the photo in a frame with a colour slab behind it.
 * Two blurred fields drift on live sites (CSS only). The frame itself floats.
 */
function Stage({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node} className="baze-hero baze-band relative isolate overflow-hidden">
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="baze-drift absolute top-[-12%] left-[8%] size-[34rem] rounded-full bg-site-primary/25 blur-3xl" />
        <div className="baze-drift-late absolute top-[28%] right-[-8%] size-[26rem] rounded-full bg-site-secondary/50 blur-3xl" />
      </div>
      <Container>
        <div className="mx-auto max-w-3xl text-center">
          <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
          <HeroTitle ctx={ctx} node={node} className="text-[2.5rem] leading-[1.02] @md:text-6xl @3xl:text-7xl @5xl:text-[5rem]" />
          <HeroDescription ctx={ctx} node={node} className="mx-auto mt-6 max-w-xl text-lg leading-relaxed text-site-muted @3xl:text-xl" />
          <HeroActions ctx={ctx} node={node} center />
          <Locale ctx={ctx} center />
        </div>
        {hasImage ? (
          <div className="baze-enter-pop relative mx-auto mt-12 max-w-4xl @3xl:mt-16" style={enterDelay(4)}>
            <div aria-hidden="true" className="absolute inset-x-6 top-8 -bottom-4 -z-10 rounded-site-lg bg-site-primary @3xl:inset-x-14" />
            <div className="baze-float overflow-hidden rounded-site-lg bg-site-surface shadow-site-lg ring-1 ring-white/15">
              <div className="aspect-[4/3] @3xl:aspect-[16/9]">
                {c.image ? (
                  <Img ctx={ctx} image={c.image} sizes="(min-width: 1024px) 896px, 100vw" priority={isPriority(ctx, node)} path="content.image" target={1600} />
                ) : (
                  <EditorImagePlaceholder ctx={ctx} path="content.image" />
                )}
              </div>
            </div>
          </div>
        ) : null}
      </Container>
    </SectionShell>
  );
}

/** Thick colour margin around a paper sheet: title on one side, one photo on the other. */
function Margin({ node, ctx }: SectionProps<"hero">) {
  const c = node.content;
  const hasImage = !!c.image || ctx.editor;
  return (
    <SectionShell ctx={ctx} node={node} padded={false} className="baze-hero baze-band relative bg-site-primary! text-site-on-primary">
      <div className="p-3 @3xl:p-5 @5xl:p-6">
        <div className="rounded-site-md bg-site-surface px-6 py-12 text-site-text @3xl:px-12 @3xl:py-16 @5xl:px-16 @5xl:py-20">
          <div className={cx("grid items-end gap-10 @3xl:gap-14", hasImage && "@4xl:grid-cols-[1.15fr_0.85fr]")}>
            <div>
              <HeroEyebrow ctx={ctx} node={node} className="mb-5" />
              <HeroTitle ctx={ctx} node={node} className="text-[2.6rem] leading-[0.96] @md:text-6xl @3xl:text-7xl @5xl:text-[5.25rem]" />
              <HeroDescription ctx={ctx} node={node} className="mt-6 max-w-xl text-lg leading-relaxed text-site-muted" />
              <HeroActions ctx={ctx} node={node} />
              <Locale ctx={ctx} />
            </div>
            {hasImage ? (
              <div className="baze-enter-pop overflow-hidden rounded-site-sm bg-site-bg" style={enterDelay(2)}>
                <div className="aspect-[4/5] @3xl:aspect-[5/6]">
                  {c.image ? (
                    <Img ctx={ctx} image={c.image} sizes="(min-width: 1024px) 520px, 100vw" priority={isPriority(ctx, node)} path="content.image" target={1400} />
                  ) : (
                    <EditorImagePlaceholder ctx={ctx} path="content.image" />
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </SectionShell>
  );
}

export function Hero(props: SectionProps<"hero">) {
  switch (props.node.variant) {
    case "margin":
      return <Margin {...props} />;
    case "stage":
      return <Stage {...props} />;
    case "marquee":
      return <Marquee {...props} />;
    case "collage":
      return <Collage {...props} />;
    case "cover":
      return <Cover {...props} />;
    case "celebration":
      return <Celebration {...props} />;
    case "profile":
      return <Profile {...props} />;
    case "centered":
      return <Centered {...props} />;
    case "image-background":
      return <ImageBackground {...props} />;
    case "minimal":
      return <Minimal {...props} />;
    case "arch":
      return <Arch {...props} />;
    case "editorial":
      return <Editorial {...props} />;
    default:
      return <Split {...props} />;
  }
}
